import { HTTPMethod } from 'helpers/method';
import { getStorage, STORAGE_KEY } from 'helpers/storage';
import { ENDPOINT } from 'services/configs/endpoint.constant';

import {
  CreateUserPayload,
  EditUserPayload,
  GetListUserResponse,
  GetRolesResponse,
  UserResponse,
} from './user.type';
import { publicRequest, request } from '../configs/axiosConfig';

/** Pull the download filename out of a Content-Disposition header (prefers RFC 5987 filename*). */
const parseContentDispositionFilename = (contentDisposition?: string): string | null => {
  if (!contentDisposition) return null;
  const utf8Match = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(contentDisposition);
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1].replace(/"/g, '').trim());
    } catch {
      return utf8Match[1].replace(/"/g, '').trim();
    }
  }
  const plainMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return plainMatch ? plainMatch[1].trim() : null;
};

export const UserService = {
  getListUsers: async () => {
    return request<GetListUserResponse>({ method: HTTPMethod.GET, url: ENDPOINT.GET_LIST_USERS });
  },
  getListRoles: async () => {
    return request<GetRolesResponse>({ method: HTTPMethod.GET, url: ENDPOINT.GET_ROLES_USER });
  },
  createUser: async (data: CreateUserPayload) => {
    return request<UserResponse>({ method: HTTPMethod.POST, url: ENDPOINT.CREATE_USER, data });
  },
  editUser: async (id: string, data: EditUserPayload) => {
    return request<UserResponse>({
      method: HTTPMethod.PUT,
      url: `${ENDPOINT.UPDATE_USER}/${id}`,
      data,
    });
  },
  deleteUser: async (id: string) => {
    return request({ method: HTTPMethod.DELETE, url: `${ENDPOINT.DELETE_USER}/${id}` });
  },
  changeLocale: async (locale: string) => {
    return request({
      method: HTTPMethod.PUT,
      url: ENDPOINT.CHANGE_LOCALE_USER,
      data: { locale },
    });
  },
  /**
   * Export organization users to a .csv file (ADMIN / SUPER_ADMIN only).
   * `org` (SUPER_ADMIN, org id) exports a specific org; omit → current org from the token.
   * Uses publicRequest (no response interceptor) + manual Bearer so we keep the full response
   * and can read the server-provided filename from Content-Disposition.
   */
  exportUsers: async (org?: string): Promise<{ blob: Blob; filename: string }> => {
    const token = getStorage(STORAGE_KEY.TOKEN);
    const response = await publicRequest({
      method: HTTPMethod.GET,
      url: ENDPOINT.EXPORT_USERS,
      params: org ? { org } : undefined,
      responseType: 'blob',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    const contentDisposition = (response.headers?.['content-disposition'] ??
      response.headers?.['Content-Disposition']) as string | undefined;
    const filename = parseContentDispositionFilename(contentDisposition) || 'users.csv';
    return { blob: response.data as Blob, filename };
  },
};
